import { useCallback, useEffect, useRef, useState } from "react"
import { v4 as uuid } from "uuid"
import type { Media } from "../interface/media"
import { useUploadMediaMutation } from "../services/publications/mediaApi"
import { currentPostBotSession, usePostBotSession } from "./usePostBotSession"

export interface MediaWriteAccess {
  readOnly?: boolean
  canWrite?: boolean
  draftKey?: string
}
export interface MediaUploadItem {
  localId: string
  /** Completed receipts keep metadata only, never the uploaded File payload. */
  file: File | Pick<File, "name" | "size" | "type">
  previewUrl?: string
  status: "queued" | "uploading" | "success" | "error"
  media?: Media
  error?: unknown
  validationError?: string
}
export const MEDIA_ACCEPT = "image/*,video/*"
/** Advisory family/size check only. The server validates actual format/signature.
 * The canonical contract does not enumerate a narrower MIME allowlist. */
export const validateMediaFile = (file: File): string | undefined => {
  const limit = file.type.startsWith("image/")
    ? 5_000_000
    : file.type.startsWith("video/")
      ? 20_000_000
      : undefined
  if (limit === undefined) return "Выберите фото или видео."
  if (file.size === 0) return "Файл пуст."
  if (file.size > limit)
    return `Размер файла превышает ${limit.toLocaleString("ru-RU")} байт.`
}

/** Files and owned blob URLs stay local, never in Redux. Removing an item only
 * forgets local work: an in-flight upload may still create a server resource. */
export function useMediaUploads(
  access: MediaWriteAccess = {},
  { retainCompletions = false }: { retainCompletions?: boolean } = {},
) {
  const session = usePostBotSession()
  const [upload] = useUploadMediaMutation()
  const [items, setItems] = useState<MediaUploadItem[]>([])
  const current = useRef(items)
  const mounted = useRef(false)
  const owned = useRef(new Set<string>())
  const writable = !access.readOnly && access.canWrite !== false
  const context = useRef({ session, draftKey: access.draftKey, writable })
  context.current = { session, draftKey: access.draftKey, writable }
  const owner = useRef({ session, draftKey: access.draftKey })
  const visibleItems =
    owner.current.session === session &&
    owner.current.draftKey === access.draftKey
      ? items
      : []
  const canMutate = () =>
    writable &&
    context.current.writable &&
    mounted.current &&
    currentPostBotSession() === session &&
    context.current.draftKey === access.draftKey
  const publish = useCallback((next: MediaUploadItem[]) => {
    current.current = next
    if (mounted.current) setItems(next)
  }, [])
  useEffect(() => {
    mounted.current = true
    owner.current = { session, draftKey: access.draftKey }
    const urls = owned.current
    publish([])
    return () => {
      mounted.current = false
      current.current = []
      for (const url of urls) URL.revokeObjectURL(url)
      urls.clear()
    }
  }, [session, access.draftKey, publish])
  // Passive cleanup runs after React removes the last card using the Blob URL.
  useEffect(() => {
    const used = new Set(items.map((item) => item.previewUrl))
    for (const url of owned.current) {
      if (!used.has(url)) {
        owned.current.delete(url)
        URL.revokeObjectURL(url)
      }
    }
  }, [items])
  useEffect(() => {
    if (!writable) return
    for (const item of current.current) {
      if (item.status !== "queued" || !(item.file instanceof File)) continue
      const file = item.file
      const attempt = { ...item, status: "uploading" as const }
      publish(
        current.current.map((value) => (value === item ? attempt : value)),
      )
      const finish = (patch: Partial<MediaUploadItem>) => {
        if (
          !mounted.current ||
          !current.current.includes(attempt) ||
          currentPostBotSession() !== session ||
          context.current.draftKey !== access.draftKey
        )
          return
        publish(
          current.current.map((value) =>
            value === attempt ? { ...attempt, ...patch } : value,
          ),
        )
      }
      void (async () => {
        try {
          const media = await upload(file).unwrap()
          finish({
            status: "success",
            media,
            error: undefined,
            file: { name: file.name, size: file.size, type: file.type },
          })
        } catch (error) {
          // Upload has no idempotency/recovery endpoint. Never automatically retry.
          finish({ status: "error", error })
        }
      })()
    }
  }, [items, writable, upload, publish, session, access.draftKey])

  // The library has no completion consumer. Pickers opt in and acknowledge only
  // after auto-attachment, retaining at most the selected preview/order receipts.
  useEffect(() => {
    if (
      !retainCompletions &&
      current.current.some((item) => item.status === "success")
    )
      publish(current.current.filter((item) => item.status !== "success"))
  }, [items, retainCompletions, publish])

  return {
    items: visibleItems,
    mediaIds: visibleItems.flatMap((item) =>
      item.media ? [item.media.id] : [],
    ),
    // A passive consumer may belong to an older render than `current`. Only
    // explicitly consumed local receipts are eligible for detached cleanup.
    retainCompleted: (
      mediaIds: readonly string[],
      consumedLocalIds: ReadonlySet<string>,
    ) => {
      if (
        !mounted.current ||
        currentPostBotSession() !== session ||
        context.current.draftKey !== access.draftKey
      )
        return
      const next = current.current.filter(
        (item) =>
          item.status !== "success" ||
          !consumedLocalIds.has(item.localId) ||
          (item.media && mediaIds.includes(item.media.id)),
      )
      if (next.length !== current.current.length) publish(next)
    },
    addFiles: (files: readonly File[]) => {
      if (!canMutate()) return
      publish([
        ...current.current,
        ...files.map((file): MediaUploadItem => {
          const validationError = validateMediaFile(file)
          const previewUrl = validationError
            ? undefined
            : URL.createObjectURL(file)
          if (previewUrl) owned.current.add(previewUrl)
          return {
            localId: uuid(),
            file,
            previewUrl,
            validationError,
            status: validationError ? "error" : "queued",
          }
        }),
      ])
    },
    retry: (localId: string) => {
      if (!canMutate()) return
      publish(
        current.current.map((item) =>
          item.localId === localId &&
          item.status === "error" &&
          !item.validationError
            ? { ...item, status: "queued", error: undefined }
            : item,
        ),
      )
    },
    remove: (localId: string) => {
      if (!canMutate()) return
      publish(current.current.filter((value) => value.localId !== localId))
    },
    move: (from: number, to: number) => {
      if (
        !canMutate() ||
        !Number.isInteger(from) ||
        !Number.isInteger(to) ||
        from < 0 ||
        from >= current.current.length ||
        to < 0 ||
        to >= current.current.length
      )
        return
      const next = [...current.current]
      next.splice(to, 0, next.splice(from, 1)[0])
      publish(next)
    },
  }
}
export type MediaUploads = ReturnType<typeof useMediaUploads>
